import {inject, Injectable} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { FirebaseInsertToken } from "../Models/firebase.model";
import { EntityInfo } from "../../../models/custom-models/entity-info";
import { Constants } from "../../../models/constants";
import { UserDetails } from "../../../models/userDetails";
import {
    AddUpdateReminderRequest,
    Alert,
    DashBoardEntitySelectionEventResponse,
    GetAllReminderForUserResponse,
    GetRemindersRequest,
    GetTodayFocusRequest,
    GetUserProgressRequest,
    OrgTeamState,
    ProgressData,
    ReminderResponse,
    ReminderStatus,
    ReminderTabIndex,
    StatusNameClickEvent,
    TodayFocusData,
} from "../Models/dashboard.model";
import { APIResponse } from "../../../models/Response";
import { OrgBUProjectTeamStructure } from "../../../models/custom-models/org-buproject-team-structure";
import { FlaggedTaskInfo } from "../../../modules/task-management/models/task-dependencies.model";
import { format, isFuture, isSameDay } from "date-fns";
import { finalize } from "rxjs/operators";
import { SharedService } from "../../../services/shared.service";
import {SecureStorageService} from "../../../modules/core/services/secure-storage.service";
import {LocalStorageRelatedData} from "../../../local-storage-related-data.model";

@Injectable({
    providedIn: "root",
})
export class DashboardService {
    ancestors: any[] = [];
    selectedFiltersFromDashBoard: BehaviorSubject<DashBoardEntitySelectionEventResponse | null> =
        new BehaviorSubject<DashBoardEntitySelectionEventResponse | null>(null);
    selectedFiltersFromDashBoard$: Observable<DashBoardEntitySelectionEventResponse | null> =
        this.selectedFiltersFromDashBoard.asObservable();

    openTasksDetailsBasedOnStatusBarClick: BehaviorSubject<StatusNameClickEvent | null> =
        new BehaviorSubject<StatusNameClickEvent | null>(null);
    openTasksDetailsBasedOnStatusBarClick$: Observable<StatusNameClickEvent | null> =
        this.openTasksDetailsBasedOnStatusBarClick.asObservable();

    openDashboardFiltersEvent: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
    openDashboardFiltersEvent$: Observable<boolean | null> = this.openDashboardFiltersEvent.asObservable();

    reminders: BehaviorSubject<GetAllReminderForUserResponse | null> =
        new BehaviorSubject<GetAllReminderForUserResponse | null>(null);
    reminders$: Observable<GetAllReminderForUserResponse | null> = this.reminders.asObservable();

    private dataTreeSubject = new BehaviorSubject<OrgTeamState>({
        orgTeamStructure: [],
        isResetAllowed: true,
    });
    public dataTree$ = this.dataTreeSubject.asObservable();

    private selectedEntitySubject = new BehaviorSubject<EntityInfo | undefined>(undefined);
    public selectedEntity$ = this.selectedEntitySubject.asObservable();

    private selectedNodeOfDashboard: OrgBUProjectTeamStructure | null = null;
    private apiServerUrl = environment.apiBaseUrl;
    private selectedEntityInfo: EntityInfo | null = null;
    private selectedEntityUsers: UserDetails[] = [];
    private flaggedTasksInfo: Map<number, FlaggedTaskInfo> = new Map();
    updateEntityStructure: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    updateEntityStructure$: Observable<boolean> = this.updateEntityStructure.asObservable();

    private disableChildrenOfTreeSubject = new BehaviorSubject<boolean>(false);
    disableChildrenOfTree$ = this.disableChildrenOfTreeSubject.asObservable();
    private secureStorageService = inject(SecureStorageService)

    constructor(
        private http: HttpClient,
        private sharedService: SharedService,
    ) {}

    triggerEntityStructureUpdate() {
        this.updateEntityStructure.next(true);
    }

    setTaskDependencies(taskDependencies: FlaggedTaskInfo[]) {
        if (taskDependencies && Array.isArray(taskDependencies)) {
            taskDependencies.forEach((taskDep) => {
                this.flaggedTasksInfo.set(taskDep.taskId, taskDep);
            });
        }
    }

    setRemindersData(reminders: GetAllReminderForUserResponse | null) {
        if (!reminders) {
            this.reminders.next(
                new GetAllReminderForUserResponse({
                    todayReminders: [],
                    futureReminders: [],
                    pastReminders: [],
                }),
            );
            return;
        } else {
            this.reminders.next(new GetAllReminderForUserResponse(reminders));
        }
    }

    hideChildrenFromNavbarTreeStructure(bool: boolean) {
        this.disableChildrenOfTreeSubject.next(bool);
    }

    setTreeData(data: OrgBUProjectTeamStructure[], isResetAllowed: boolean) {
        let result: OrgTeamState = new OrgTeamState();
        result.orgTeamStructure = data;
        result.isResetAllowed = isResetAllowed;
        this.dataTreeSubject.next(result);
    }

    getDashBoardEntitiesTreeData() {
        return this.dataTreeSubject.getValue();
    }

    setSelectedEntityInfo(entityInfo: EntityInfo | undefined) {
        this.selectedEntitySubject.next(entityInfo);
    }

    clearRemindersData() {
        this.reminders.next(null);
    }

    getFlaggedTasksInfo() {
        return Array.from(this.flaggedTasksInfo.values());
    }

    detectFirebaseToken(deviceType: string, deviceId: string): Observable<any> {
        return this.http.get(`${this.apiServerUrl}/firebase-token/get-token/${deviceType}/${deviceId}`);
    }

    insertFirebaseToken(fcmInsertTokenRequestBody: FirebaseInsertToken): Observable<any> {
        return this.http.post(`${this.apiServerUrl}/firebase-token/insert-token`, fcmInsertTokenRequestBody);
    }

    /** Method to store the selected entity info and ancestors in the Navbar
     * @returns void
     * @param selectedEntityInfo : EntityInfo
     * @param ancestors : any[]
     * @param node : OrgBUProjectTeamStructure
     * if the selectedEntityInfo is same as the previous selectedEntityInfo then return
     * else store the selectedEntityInfo and ancestors in the selectedFiltersFromDashBoard BehaviorSubject
     *  */
    storeSelectedEntityInfo(selectedEntityInfo: EntityInfo | null, ancestors: any[], node: OrgBUProjectTeamStructure) {
        if (
            this.selectedEntityInfo &&
            selectedEntityInfo &&
            this.selectedEntityInfo.entityId === selectedEntityInfo.entityId &&
            this.selectedEntityInfo.entityTypeId === selectedEntityInfo.entityTypeId
        ) {
            return;
        }
        this.selectedEntityInfo = selectedEntityInfo;
        this.ancestors = ancestors;
        this.selectedNodeOfDashboard = node;
        this.selectedFiltersFromDashBoard.next({
            data: selectedEntityInfo,
        });
    }

    isSingleNonPersonalOrgPresent(optionalData?: OrgBUProjectTeamStructure[]): OrgBUProjectTeamStructure | false {
        const data = optionalData ? optionalData : this.getDashBoardEntitiesTreeData().orgTeamStructure;
        if (data.length == 1) {
            const org = data[0];
            if (org.orgId !== Constants.EntityTypes.PERSONAL_ORG_ID) {
                return org;
            } else {
                return false;
            }
        }
        return false;
    }

    isPersonalAndSingleOrgPresent(optionalData?: OrgBUProjectTeamStructure[]): OrgBUProjectTeamStructure | false {
        const data = optionalData ? optionalData : this.getDashBoardEntitiesTreeData().orgTeamStructure;
        if (data.length == 2) {
            const personalOrg = data.find((org) => org.orgId === Constants.EntityTypes.PERSONAL_ORG_ID);
            const nonPersonalOrg = data.find((org) => org.orgId !== Constants.EntityTypes.PERSONAL_ORG_ID);
            if (personalOrg && nonPersonalOrg) {
                return nonPersonalOrg;
            } else {
                return false;
            }
        }
        return false;
    }

    storeSelectedEntityUsers(users: UserDetails[]) {
        this.selectedEntityUsers = users;
    }

    resetSelectedEntityInfo() {
        this.selectedNodeOfDashboard = null;
        this.selectedEntityInfo = null;
        this.ancestors = [];
        this.selectedFiltersFromDashBoard.next({ data: null });
    }

    getOrgIdOfSelectedEntity() {
        if (this.selectedEntityInfo && this.selectedEntityInfo.entityTypeId === Constants.EntityTypes.ORG) {
            return this.selectedEntityInfo.entityId;
        } else if (this.selectedEntityInfo && this.selectedEntityInfo.entityTypeId === Constants.EntityTypes.TEAM) {
            return this.ancestors[0].id;
        } else if (this.selectedEntityInfo && this.selectedEntityInfo.entityTypeId === Constants.EntityTypes.PROJECT) {
            return this.ancestors[0].id;
        } else {
            return null;
        }
    }

    getSelectedOrgFromDashBoard() {
        if (this.selectedEntityInfo && this.selectedEntityInfo.entityTypeId === Constants.EntityTypes.ORG) {
            return this.selectedEntityInfo;
        } else {
            return null;
        }
    }

    getSelectedTeamFromDashBoard() {
        if (this.selectedEntityInfo && this.selectedEntityInfo.entityTypeId === Constants.EntityTypes.TEAM) {
            return this.selectedEntityInfo;
        } else {
            return null;
        }
    }

    getSelectedProjectFromDashBoard() {
        if (this.selectedEntityInfo && this.selectedEntityInfo.entityTypeId === Constants.EntityTypes.PROJECT) {
            return this.selectedEntityInfo;
        } else {
            return null;
        }
    }

    getSelectedEntityUsers() {
        return this.selectedEntityUsers;
    }

    getUserProgress(
        getUserProgressRequest: GetUserProgressRequest,
        accountIds: string,
    ): Observable<APIResponse<ProgressData>> {
        return this.http.post<APIResponse<ProgressData>>(
            `${this.apiServerUrl}/stats/getUserProgress`,
            getUserProgressRequest,
            {
                headers: {
                    accountIds: accountIds,
                },
            },
        );
    }

    getTodayFocus(request: GetTodayFocusRequest, allAccountIds: string): Observable<APIResponse<TodayFocusData>> {
        return this.http.post<APIResponse<TodayFocusData>>(`${this.apiServerUrl}/stats/getTodayFocus`, request, {
            headers: {
                accountIds: allAccountIds,
            },
        });
    }

    //     /api/capacity/getTeamCapacity/337

    getTeamCapacity(teamId: number, accountId: string): Observable<APIResponse<any>> {
        return this.http.get<APIResponse<any>>(`${this.apiServerUrl}/capacity/getTeamCapacity/${teamId}`, {
            headers: {
                accountIds: accountId,
            },
        });
    }

    storeLocallySelectedViewMode(viewMode: number) {
        this.secureStorageService.setItem(LocalStorageRelatedData.DASHBOARD_VIEW_MODE, viewMode);
    }

    getLocallyStoredViewMode() {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.DASHBOARD_VIEW_MODE);
    }

    addReminder(reminderData: AddUpdateReminderRequest, accountIds: string): Observable<APIResponse<ReminderResponse>> {
        return this.http.post<APIResponse<ReminderResponse>>(
            `${this.apiServerUrl}/reminder/addReminder`,
            reminderData,
            {
                headers: {
                    accountIds: accountIds,
                },
            },
        );
    }

    getAllRemindersForDate(
        getRemindersRequest: GetRemindersRequest,
        accountIds: string,
    ): Observable<APIResponse<ReminderResponse[]>> {
        return this.http.post<APIResponse<ReminderResponse[]>>(
            `${this.apiServerUrl}/reminder/getRemindersForDate`,
            getRemindersRequest,
            {
                headers: {
                    accountIds: accountIds,
                },
            },
        );
    }

    getUserAllReminder(accountIds: string): Observable<APIResponse<GetAllReminderForUserResponse>> {
        return this.http.get<APIResponse<GetAllReminderForUserResponse>>(
            `${this.apiServerUrl}/reminder/getUserAllReminder`,
            {
                headers: {
                    accountIds: accountIds,
                },
            },
        );
    }

    updateReminder(
        reminderId: number,
        addReminderRequest: AddUpdateReminderRequest,
        allAccountIdsOfLoggedInUser: string,
    ) {
        return this.http.post<APIResponse<ReminderResponse>>(
            `${this.apiServerUrl}/reminder/updateReminder/${reminderId}`,
            addReminderRequest,
            {
                headers: {
                    accountIds: allAccountIdsOfLoggedInUser,
                },
            },
        );
    }

    deleteReminder(reminderId: number, allAccountIdsOfLoggedInUser: string) {
        return this.http.post<APIResponse<string>>(
            `${this.apiServerUrl}/reminder/deleteReminder/${reminderId}`,
            {},
            {
                headers: {
                    accountIds: allAccountIdsOfLoggedInUser,
                },
            },
        );
    }

    getUserAlertsToView(accountIds: string): Observable<APIResponse<Alert[]>> {
        return this.http.get<APIResponse<Alert[]>>(`${this.apiServerUrl}/alert/getUserAlertsToView`, {
            headers: {
                accountIds: accountIds,
            },
        });
    }

    openDashboardFilters(action: boolean = true) {
        this.openDashboardFiltersEvent.next(action);
    }

    emitOpenTasksDetailsBasedOnStatusBarClick(status: StatusNameClickEvent | null) {
        this.openTasksDetailsBasedOnStatusBarClick.next(status);
    }

    getOriginalSelectedNode() {
        return this.selectedNodeOfDashboard;
    }

    /**
     * Below Methods 1. getDefaultFromDateForOverDashBoardFilters and 2. getDefaultToDateForOverDashBoardFilters
     * are used to get the default dates for the dashboard stats value.
     * */
    getDefaultFromDateForOverDashBoardFilters() {
        let fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - Constants.StatsRequestConstants.DEFAULT_FROM_DATE_DAYS);
        return format(fromDate, "yyyy-MM-dd");
    }

    getDefaultToDateForOverDashBoardFilters() {
        let toDate = new Date();
        toDate.setDate(toDate.getDate() + Constants.StatsRequestConstants.DEFAULT_TO_DATE_DAYS);
        return format(toDate, "yyyy-MM-dd");
    }

    markReminderAsDone(reminder: ReminderResponse, accountIds: string) {
        let addReminderRequest = new AddUpdateReminderRequest();
        addReminderRequest.reminderTitle = reminder.reminderTitle;
        addReminderRequest.description = reminder.description;
        addReminderRequest.reminderDate = reminder.reminderDate;
        addReminderRequest.reminderTime = reminder.reminderTime;
        addReminderRequest.reminderStatus = ReminderStatus.COMPLETED;
        addReminderRequest.accountIdCreator = reminder.userDetails.accountId;
        // Show the spinner
        this.sharedService.showSpinner();
        // Return the observable from the updateReminder method
        return this.updateReminder(reminder.reminderId, addReminderRequest, accountIds).pipe(
            // Hide spinner on both success and error
            finalize(() => this.sharedService.hideSpinner()),
        );
    }

    findReminderIndexBasedOnDate(remindersDate: string): number {
        const reminderDate = new Date(remindersDate);
        if (isSameDay(reminderDate, new Date())) {
            return ReminderTabIndex.TODAY;
        } else if (isFuture(reminderDate) && !isSameDay(reminderDate, new Date())) {
            return ReminderTabIndex.FUTURE;
        } else {
            return ReminderTabIndex.PAST;
        }
    }

    updateReminderFromDashboard(reminderResponse: ReminderResponse, reminderTabIndex: ReminderTabIndex) {
        // Get the current reminders value
        const reminders = this.reminders.getValue();
        if (!reminders) {
            return;
        }

        // Helper function to remove a reminder from a list
        const removeReminder = (reminderList: ReminderResponse[], reminderId: number) => {
            return reminderList.filter((reminder) => reminder.reminderId !== reminderId);
        };

        // Helper function to add or update a reminder in a list
        const updateOrAddReminder = (reminderList: ReminderResponse[], newReminder: ReminderResponse) => {
            const foundIndex = reminderList.findIndex((reminder) => reminder.reminderId === newReminder.reminderId);
            if (foundIndex !== -1) {
                reminderList[foundIndex] = new ReminderResponse(newReminder); // Update existing reminder
            } else {
                reminderList.push(new ReminderResponse(newReminder)); // Add new reminder
            }
            return reminderList;
        };

        // Remove the reminder from the old list based on the current tab index
        switch (reminderTabIndex) {
            case ReminderTabIndex.TODAY:
                reminders.todayReminders = removeReminder(reminders.todayReminders, reminderResponse.reminderId);
                break;
            case ReminderTabIndex.FUTURE:
                reminders.futureReminders = removeReminder(reminders.futureReminders, reminderResponse.reminderId);
                break;
            case ReminderTabIndex.PAST:
                reminders.pastReminders = removeReminder(reminders.pastReminders, reminderResponse.reminderId);
                break;
        }

        // Find the new tab index based on the new reminder date
        const newTabIndex = this.findReminderIndexBasedOnDate(reminderResponse.reminderDate);

        // Add the updated reminder to the correct list based on the new date
        switch (newTabIndex) {
            case ReminderTabIndex.TODAY:
                reminders.todayReminders = updateOrAddReminder(reminders.todayReminders, reminderResponse);
                break;
            case ReminderTabIndex.FUTURE:
                reminders.futureReminders = updateOrAddReminder(reminders.futureReminders, reminderResponse);
                break;
            case ReminderTabIndex.PAST:
                reminders.pastReminders = updateOrAddReminder(reminders.pastReminders, reminderResponse);
                break;
        }

        // Set the updated reminders value
        this.reminders.next(reminders);
    }

    addNewReminderFromDashboard(reminderResponse: ReminderResponse) {
        const reminderTabIndex = this.findReminderIndexBasedOnDate(reminderResponse.reminderDate);
        console.log("Reminder Tab Index", reminderTabIndex);
        //     get the value of reminders
        const reminders = this.reminders.getValue();
        if (!reminders) {
            return;
        }
        switch (reminderTabIndex) {
            case ReminderTabIndex.TODAY:
                reminders.todayReminders.push(new ReminderResponse(reminderResponse));
                console.log("Today Reminders", reminders.todayReminders);
                break;
            case ReminderTabIndex.FUTURE:
                reminders.futureReminders.push(new ReminderResponse(reminderResponse));
                console.log("Future Reminders", reminders.futureReminders);
                break;
            case ReminderTabIndex.PAST:
                reminders.pastReminders.push(new ReminderResponse(reminderResponse));
                console.log("Past Reminders", reminders.pastReminders);
                break;
        }

        //     set the updated reminders value
        this.reminders.next(reminders);
    }
}
